<template>
  <section v-if="show" class="insights" data-test="insights-section">
    <header class="insights-head">
      <div class="insights-title-block">
        <h2 class="insights-title">Sprint readout</h2>
        <span class="insights-sub" data-test="insights-meta">
          AI synthesis · {{ modelLabel }} · {{ computedAtLabel }}
        </span>
      </div>
      <span v-if="hasError" class="insights-error" :title="audit?.error" data-test="insights-error">
        AI generation failed — falling back to raw metrics.
      </span>
    </header>

    <p v-if="audit?.headline" class="insights-headline" data-test="insights-headline">
      {{ audit.headline }}
    </p>

    <div class="insights-grid">
      <article
        v-for="block in nonEmptyBlocks"
        :key="block.key"
        class="insights-block"
        :class="block.tone"
        :data-test="`insights-block-${block.key}`"
      >
        <h3 class="block-title">{{ block.title }}</h3>
        <ul class="block-list">
          <li v-for="(item, i) in block.items" :key="i">{{ item }}</li>
        </ul>
      </article>
    </div>

    <details v-if="audit?.raw_text" class="insights-raw" data-test="insights-raw">
      <summary>Model returned unstructured text — show raw output</summary>
      <pre>{{ audit.raw_text }}</pre>
    </details>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { InsightsAudit } from "@/api/types";

const props = defineProps<{ audit?: InsightsAudit }>();

const audit = computed<InsightsAudit | null>(() => props.audit ?? null);

const hasContent = computed(() => {
  const a = audit.value;
  if (!a) return false;
  return Boolean(
    a.headline
    || a.what_went_well.length
    || a.top_risks.length
    || a.recommended_actions.length
    || a.watch_next_sprint.length
    || a.raw_text,
  );
});

const hasError = computed(() => Boolean(audit.value?.error));

const show = computed(() => Boolean(audit.value && (hasContent.value || hasError.value)));

const blocks = computed(() => {
  const a = audit.value;
  if (!a) return [];
  return [
    { key: "well",    title: "What went well",        tone: "good", items: a.what_went_well },
    { key: "risks",   title: "Top risks",             tone: "bad",  items: a.top_risks },
    { key: "actions", title: "Recommended actions",   tone: "warn", items: a.recommended_actions },
    { key: "watch",   title: "Watch for next sprint", tone: "info", items: a.watch_next_sprint },
  ];
});

const nonEmptyBlocks = computed(() => blocks.value.filter((b) => b.items.length > 0));

const modelLabel = computed(() => {
  const m = audit.value?.model ?? "";
  // Strip "eu.anthropic." prefix for readability — render "claude-sonnet-4-6".
  return m.replace(/^[a-z]+\./, "").replace(/^anthropic\./, "");
});

const computedAtLabel = computed(() => {
  const t = audit.value?.computed_at;
  if (!t) return "";
  try {
    return new Date(t).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return t;
  }
});
</script>

<style scoped>
.insights {
  background: var(--surface-card, #fff);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-left: 4px solid #7c3aed;
  border-radius: 8px;
  padding: 16px 18px;
  display: flex; flex-direction: column; gap: 14px;
}

.insights-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.insights-title-block { display: flex; flex-direction: column; gap: 2px; }
.insights-title { margin: 0; font-size: 15px; font-weight: 700; color: var(--text-color, #111827); }
.insights-sub { font-size: 11px; color: var(--text-muted, #6b7280); text-transform: uppercase; letter-spacing: 0.04em; }
.insights-error { font-size: 12px; color: #b91c1c; }

.insights-headline {
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: var(--text-color, #111827);
  font-weight: 500;
  padding: 8px 12px;
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.08), rgba(37, 99, 235, 0.06));
  border-radius: 6px;
}

.insights-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}
.insights-block {
  border: 1px solid var(--surface-border, #e5e7eb);
  border-left-width: 3px;
  border-radius: 6px;
  padding: 10px 12px;
  background: var(--surface-card, #fff);
}
.insights-block.good { border-left-color: #15803d; }
.insights-block.bad  { border-left-color: #b91c1c; }
.insights-block.warn { border-left-color: #d97706; }
.insights-block.info { border-left-color: #2563eb; }

.block-title {
  margin: 0 0 6px 0; font-size: 12px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-muted, #6b7280);
}
.block-list { margin: 0; padding-left: 18px; display: flex; flex-direction: column; gap: 4px; }
.block-list li { font-size: 13px; line-height: 1.45; color: var(--text-color, #111827); }

.insights-raw {
  font-size: 12px; color: var(--text-muted, #6b7280);
  background: var(--surface-elev, #f9fafb);
  border-radius: 6px; padding: 8px 10px;
}
.insights-raw summary { cursor: pointer; }
.insights-raw pre { margin: 6px 0 0; white-space: pre-wrap; font-family: inherit; font-size: 12px; }
</style>
